import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Login.css';

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get email from URL params (if coming from registration) or use empty
  const emailFromUrl = searchParams.get('email') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!emailFromUrl) {
      setError('Email address is required. Please go to the registration page.');
      setLoading(false);
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.verifyEmail(emailFromUrl, verificationCode);
      
      if (response.data.success) {
        setSuccess(response.data.message || 'Email verified successfully!');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to verify email.');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to verify code. The code may be invalid or expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Verify Email" description="Verify your GuyRules account email with the code we sent you." canonicalPath="/verify-email" />
      <Header />
      <div className="container">
        <div className="login-page">
          <h1>Verify Email</h1>
          
          {emailFromUrl && (
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Enter the 6-digit verification code sent to <strong>{emailFromUrl}</strong>
            </p>
          )}

          {!emailFromUrl && (
            <div className="alert alert-warning" style={{ 
              padding: '1rem', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107', 
              borderRadius: '4px',
              color: '#856404',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0 }}>No email address found. Please register first or use the resend verification page.</p>
            </div>
          )}

          {error && (
            <div className="error-message" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" style={{ 
              padding: '1rem', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              borderRadius: '4px',
              color: '#155724',
              marginBottom: '1rem'
            }}>
              {success}
              <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                Redirecting to login page...
              </p>
            </div>
          )}

          {emailFromUrl && !success && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  type="text"
                  id="verificationCode"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setError('');
                  }}
                  required
                  maxLength={6}
                  disabled={loading}
                  placeholder="000000"
                  style={{
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                  autoComplete="off"
                  autoFocus
                />
                <small>Enter the 6-digit code from your email</small>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || verificationCode.length !== 6}
                style={{ width: '100%', marginBottom: '0.5rem' }}
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/resend-verification')}
                style={{ width: '100%' }}
              >
                Resend Code
              </button>
            </form>
          )}

          <p className="register-link" style={{ marginTop: '1rem' }}>
            <Link to="/login">Back to Login</Link> | <Link to="/register">Register</Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default VerifyEmail;
